import {
  NativeFieldExtra,
  SalesforceMappingOption,
  SalesforceResourceMappingLocalData,
} from 'module/api/integrations/salesforce/types';
import { IconShell } from 'module/ui/components/atoms/icon-shell';
import { faSalesforce } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  type PopoverSelectGroup,
  type PopoverSelectOption,
} from '@rocketlane/ui';
import { Space } from 'antd';
import { FormattedMessage } from 'react-intl';
import {
  getFieldIcon,
  getUiFieldType,
} from 'components/organisms/fields/field-types';
import { RocketlaneFieldValue } from './rocketlane-field-value';
import { SalesforceFieldValue } from './salesforce-field-value';

export type FieldSelectType = 'salesforce' | 'rocketlane';

interface FieldSelectTriggerProps {
  loading?: boolean;
  triggerLabel?: React.ReactNode;
  type: FieldSelectType;
  value?: PopoverSelectOption | PopoverSelectOption[] | null;
}

const getDisplayLabel = (label: string) =>
  label.includes(']') ? label.split(']')[1].trim() : label;

export const buildFieldSelectGroups = (
  options: SalesforceMappingOption[],
  fieldObject: SalesforceResourceMappingLocalData,
  fieldsLabel: string,
): PopoverSelectGroup[] => {
  const groupedOptions = new Map<string, PopoverSelectOption[]>();

  options.forEach((option) => {
    const groupLabel =
      option.parent ?? fieldObject.externalResourceType ?? fieldsLabel;
    const items = groupedOptions.get(groupLabel) ?? [];

    items.push({
      label: getDisplayLabel(option.label),
      value: option.value,
    });
    groupedOptions.set(groupLabel, items);
  });

  const groups = Array.from(groupedOptions, ([label, items]) => ({
    key: label,
    label,
    items,
  }));
  const preferredLabel = groupedOptions.has(fieldObject.externalResourceType)
    ? fieldObject.externalResourceType
    : groupedOptions.has('Rocketlane')
      ? 'Rocketlane'
      : undefined;

  if (preferredLabel == null) {
    return groups;
  }

  return [
    ...groups.filter((group) => group.label === preferredLabel),
    ...groups.filter((group) => group.label !== preferredLabel),
  ];
};

export const resolveFieldSelectOption = <Extra,>(
  selected: PopoverSelectOption | PopoverSelectOption[] | null,
  options: SalesforceMappingOption<Extra>[],
): SalesforceMappingOption<Extra> | null => {
  if (selected == null || Array.isArray(selected)) {
    return null;
  }

  const source = options.find((option) => option.value === selected.value);

  return source == null ? null : { ...source, label: selected.label };
};

export const renderSalesforceFieldOptionIcon = () => (
  <FontAwesomeIcon icon={faSalesforce} color="var(--icon-primary)" />
);

export const renderRocketlaneFieldOptionIcon = (
  option: PopoverSelectOption,
  options: SalesforceMappingOption<NativeFieldExtra>[],
) => {
  const source = options.find((candidate) => candidate.value === option.value);

  if (source?.extra == null) {
    return null;
  }

  const fieldType = getUiFieldType(source.extra);

  return <IconShell size="xsmall" icon={getFieldIcon(fieldType)} />;
};

export const renderFieldSelectTrigger = ({
  loading,
  triggerLabel,
  type,
  value,
}: FieldSelectTriggerProps) => {
  const selectedValue = Array.isArray(value) ? null : value;

  if (selectedValue != null) {
    return (
      <Button
        variant="link"
        loading={loading}
        data-cy={
          type === 'salesforce'
            ? `integrations.salesforce_field_value_add_${selectedValue.label}`
            : 'integrations.rocketlane_field_value_add'
        }
      >
        {type === 'salesforce' ? (
          <SalesforceFieldValue label={selectedValue.label} />
        ) : (
          <RocketlaneFieldValue label={selectedValue.label} />
        )}
      </Button>
    );
  }

  return (
    <Button
      variant="link"
      loading={loading}
      data-cy={
        type === 'salesforce'
          ? 'integrations.salesforce.field_add_button'
          : 'integrations.rocketlane.field_add_button'
      }
    >
      {type === 'salesforce' ? (
        <Space size="small">
          <FontAwesomeIcon icon={faSalesforce} color="var(--icon-primary)" />
          {triggerLabel ?? <FormattedMessage id="add_field" />}
        </Space>
      ) : (
        (triggerLabel ?? <FormattedMessage id="add_field" />)
      )}
    </Button>
  );
};
